import type { ReactElement } from 'react';
import { ScrollArea, type ChartConfig } from '@herit-fe/heritage-ui';

import { CartesianPlot, type PlotMargin } from './cartesian-plot';
import type { ResolvedValueAxis, Row } from './chart-series';
import { StickyValueAxis } from './sticky-value-axis';
import type { ChartSeries, XAxisOptions } from './types';

/** 상단 값축 헤더 높이(px) — 가로 막대 스크롤 판정도 이 값을 뺀 plot 영역 기준(orchestrator 공유 export). */
export const AXIS_HEADER_HEIGHT = 22;
/** plot 여백 — right 는 ScrollArea 오버레이 스크롤바 gutter(헤더도 동일 값 공유해 정렬 불변). */
const MARGIN: PlotMargin = { top: 0, right: 16, left: 4, bottom: 0 };

export interface ScrollFrameProps {
  config: ChartConfig;
  data: Row[];
  series: ChartSeries[];
  xLabels: string[];
  axis: ResolvedValueAxis;
  /** 카테고리 1행(밴드) 높이(px) — 스크롤 축 방향 per-category 크기. */
  band: number;
  /** 고정 뷰포트 높이(px) — 세로 스크롤 뷰포트. */
  viewport: number;
  /** 카테고리(행 라벨) 실측 폭(px) — orchestrator(CartesianChart)가 direct 경로와 공유해 산출·주입. */
  categoryWidth: number;
  maxBarSize?: number;
  highlightOnHover?: boolean;
  xAxis?: XAxisOptions;
  tooltipEl: ReactElement | null;
}

/**
 * ScrollFrame — **가로 막대**가 행이 많아 뷰포트를 넘칠 때의 스크롤 래퍼. 값축(x=값)을 스크롤 밖 상단에
 * `StickyValueAxis`(placement='top')로 고정하고, plot 은 `CartesianPlot valueAxisHidden` 으로 넘겨(값축
 * 그리드·도메인·틱을 sticky 와 공유해 정렬 불변) `ScrollArea` 안에서 세로 스크롤한다.
 *
 * 정렬 불변식: 헤더와 plot 이 `leadOffset`(=카테고리 폭+좌 margin)·`trailOffset`(=우 margin)·`domain` 을
 * 단일 변수로 공유 → 값→픽셀 매핑 대수적 동일(drift 불가). (세로 막대는 recharts 반응형이라 스크롤 안 씀.)
 */
export function ScrollFrame({
  config,
  data,
  series,
  xLabels,
  axis,
  band,
  viewport,
  categoryWidth,
  maxBarSize,
  highlightOnHover,
  xAxis,
  tooltipEl,
}: ScrollFrameProps) {

  return (
    <div className="w-full">
      <StickyValueAxis
        leadOffset={categoryWidth + MARGIN.left}
        trailOffset={MARGIN.right}
        domain={axis.domain}
        ticks={axis.ticks}
        extent={AXIS_HEADER_HEIGHT}
        format={axis.tickFormatter}
      />
      {/* `viz-scroll-frame` = 스크롤바 인셋 정책 마커. viz 는 패키지라 앱 래퍼(shared/ui/scroll-area.tsx)를
          import 할 수 없어(패키지→앱 역전 금지) heritage ScrollArea 를 직접 쓰되, 동일 인셋을 admin globals.css
          plain CSS(`.viz-scroll-frame`)로 받는다. */}
      <ScrollArea className="viz-scroll-frame" style={{ height: viewport - AXIS_HEADER_HEIGHT }}>
        {/* plot 높이 = 행수 × band(고정 밴드) → 뷰포트 초과 시 세로 스크롤. */}
        <div style={{ height: xLabels.length * band }}>
          <CartesianPlot
            orientation="horizontal"
            config={config}
            data={data}
            series={series}
            axis={axis}
            valueAxisHidden
            margin={MARGIN}
            categoryAxisSize={categoryWidth}
            maxBarSize={maxBarSize}
            highlightOnHover={highlightOnHover}
            xAxis={xAxis}
            tooltipEl={tooltipEl}
          />
        </div>
      </ScrollArea>
    </div>
  );
}
